import React from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useSeo } from '../hooks/useSeo';
import { useOverlayFonts } from '../hooks/useOverlayFonts';
import { useCoworkOverlay } from '../cowork/useCoworkOverlay';
import { CoworkOverlayName } from '../cowork/types';
import {
  CommunityBlock,
  CoworkOverlayGlobalStyle,
  FocusBlock,
  FullOverlay,
  TasksBlock,
  TimerBlock,
} from '../cowork/CoworkOverlayViews';

const VALID_OVERLAYS: CoworkOverlayName[] = ['timer', 'focus', 'tasks', 'community', 'full'];

const CoworkOverlayPage: React.FC = () => {
  useSeo({ title: 'Cowork overlay', noIndex: true });
  useOverlayFonts();

  const params = useParams<{ overlayKey: string; overlay: string }>();
  const [searchParams] = useSearchParams();

  const overlay = (VALID_OVERLAYS.includes(params.overlay as CoworkOverlayName)
    ? params.overlay
    : 'timer') as CoworkOverlayName;
  const demo = searchParams.get('demo') === '1';

  const { style, elements, state, remainingMs, celebrations, error } = useCoworkOverlay(
    params.overlayKey,
    overlay,
    demo,
  );

  if (error) {
    return (
      <>
        <CoworkOverlayGlobalStyle />
        <div style={{ color: '#ff6b6b', fontFamily: 'sans-serif', padding: '1rem', fontSize: 14 }}>
          {error}
        </div>
      </>
    );
  }

  if (!style || !state) {
    return <CoworkOverlayGlobalStyle />;
  }

  const justify = style.align === 'right' ? 'flex-end' : style.align === 'center' ? 'center' : 'flex-start';
  const scale = Math.max(0.3, Math.min(3, (style.scale || 100) / 100));

  // Ancho fijo del panel: así la fuente de OBS no queda enorme y no hay que reescalarla a mano.
  const width = overlay === 'full' ? 0 : Math.max(0, parseInt(elements.width, 10) || 0);

  const body = (() => {
    switch (overlay) {
      case 'focus':
        return (
          <FocusBlock
            style={style}
            elements={elements}
            focusTask={state.focusTask}
            timer={state.timer}
            remainingMs={remainingMs}
          />
        );
      case 'tasks':
        return <TasksBlock style={style} elements={elements} tasks={state.streamerTasks} />;
      case 'community':
        return (
          <CommunityBlock
            style={style}
            elements={elements}
            community={state.community}
            celebrations={celebrations}
          />
        );
      case 'full':
        return (
          <FullOverlay
            style={style}
            elements={elements}
            state={state}
            remainingMs={remainingMs}
            celebrations={celebrations}
          />
        );
      case 'timer':
      default:
        return (
          <TimerBlock
            style={style}
            elements={elements}
            timer={state.timer}
            remainingMs={remainingMs}
            focusTask={state.focusTask}
          />
        );
    }
  })();

  return (
    <>
      <CoworkOverlayGlobalStyle />
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: justify,
          padding: 8,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            transform: `scale(${scale})`,
            transformOrigin: `top ${style.align === 'right' ? 'right' : style.align === 'center' ? 'center' : 'left'}`,
            width: overlay === 'full' ? '100%' : width > 0 ? width : 'auto',
            maxWidth: '100%',
            minWidth: overlay === 'full' || width > 0 ? undefined : 320,
          }}
        >
          {body}
        </div>
      </div>
    </>
  );
};

export default CoworkOverlayPage;
